import type { UserOut } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

interface Props {
  users: UserOut[];
  loading: boolean;
  selectedUserId: string | null;
  currentUserId: string;
  onSelectUser: (id: string | null) => void;
  onEdit: (user: UserOut) => void;
  onResetPassword: (user: UserOut) => void;
  onToggleActive: (user: UserOut) => void;
}

const COL_TEMPLATE =
  "minmax(8rem,1.4fr) minmax(10rem,1.6fr) 6rem 5rem 9rem 9rem minmax(10rem,auto)";

export function UsersTable({
  users,
  loading,
  selectedUserId,
  currentUserId,
  onSelectUser,
  onEdit,
  onResetPassword,
  onToggleActive,
}: Props) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
      <div
        className="grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
        style={{ gridTemplateColumns: COL_TEMPLATE }}
      >
        <span>Nome</span>
        <span>Email</span>
        <span>Status</span>
        <span>Admin</span>
        <span>Último login</span>
        <span>Criado</span>
        <span className="text-right">Ações</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && users.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-ink-500">
            Carregando…
          </div>
        )}
        {!loading && users.length === 0 && (
          <div className="flex items-center justify-center py-12 text-sm text-ink-500">
            Nenhum usuário cadastrado.
          </div>
        )}
        {users.map((user) => {
          const isSelected = user.id === selectedUserId;
          const isSelf = user.id === currentUserId;
          return (
            <div
              key={user.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectUser(isSelected ? null : user.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelectUser(isSelected ? null : user.id);
                }
              }}
              className={`grid w-full cursor-pointer items-center gap-x-3 border-b border-ink-800 px-3 py-2.5 text-left text-xs transition-colors last:border-b-0 hover:bg-ink-800 ${
                isSelected ? "bg-ink-800" : ""
              }`}
              style={{ gridTemplateColumns: COL_TEMPLATE }}
            >
              <span className="truncate font-medium text-ink-100" title={user.name}>
                {user.name}
              </span>
              <span className="truncate text-ink-300" title={user.email}>
                {user.email}
              </span>
              <span>
                {user.is_active ? (
                  <span className="inline-block rounded border border-emerald-800 bg-emerald-900/40 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">
                    Ativo
                  </span>
                ) : (
                  <span className="inline-block rounded border border-ink-600 bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium text-ink-400">
                    Inativo
                  </span>
                )}
              </span>
              <span>
                {user.is_admin ? (
                  <span className="inline-block rounded border border-violet-800 bg-violet-900/40 px-1.5 py-0.5 text-[10px] font-medium text-violet-200">
                    Sim
                  </span>
                ) : (
                  <span className="text-ink-500">—</span>
                )}
              </span>
              <span className="text-ink-400">{formatDateTime(user.last_login_at)}</span>
              <span className="text-ink-400">{formatDateTime(user.created_at)}</span>
              <div className="flex flex-wrap justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => onEdit(user)}
                  className="rounded border border-ink-600 bg-ink-850 px-2 py-1 text-[10px] font-medium text-ink-200 hover:bg-ink-700"
                >
                  Editar
                </button>
                <button
                  type="button"
                  onClick={() => onResetPassword(user)}
                  className="rounded border border-ink-600 bg-ink-850 px-2 py-1 text-[10px] font-medium text-ink-200 hover:bg-ink-700"
                >
                  Senha
                </button>
                <button
                  type="button"
                  disabled={isSelf}
                  title={isSelf ? "Não é possível alterar seu próprio status aqui." : undefined}
                  onClick={() => onToggleActive(user)}
                  className="rounded border border-ink-600 bg-ink-850 px-2 py-1 text-[10px] font-medium text-ink-200 hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {user.is_active ? "Desativar" : "Ativar"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
